import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Table, Button, Modal,
  Tag, Badge, Statistic, Input, Select,
  Space, Descriptions, message, Avatar, Spin
} from 'antd';
import {
  EyeOutlined, StopOutlined, CheckCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  collection, getDocs, doc, updateDoc,
  query, orderBy, serverTimestamp, addDoc
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CARD_STYLE, GOLD_GRADIENT } from '../../constants/theme';

const { Option } = Select;
const { Search } = Input;

const USER_TYPES = {
  homeowner: { label: 'HomeOwner', color: '#1890FF' },
  dealer: { label: 'Dealer', color: '#C9A84C' },
  contractor: { label: 'Contractor', color: '#52C41A' },
  builder: { label: 'Builder', color: '#993556' },
  supplier: { label: 'Supplier', color: '#534AB7' },
  delivery_agent: { label: 'Delivery Agent', color: '#993C1D' },
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [actionModal, setActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.()?.toLocaleDateString('en-IN') || 'N/A',
      }));
      setUsers(data);
    } catch (error) {
      message.error('Failed to load users');
    }
    setLoading(false);
  };

  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    suspended: users.filter(u => u.status === 'suspended').length,
    dealers: users.filter(u => u.type === 'dealer').length,
  };

  const filtered = users.filter(u => {
    const matchSearch = !searchText ||
      u.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      u.phone?.includes(searchText) ||
      u.email?.toLowerCase().includes(searchText.toLowerCase());
    const matchType = filterType === 'all' || u.type === filterType;
    const matchStatus = filterStatus === 'all' || u.status === filterStatus;
    return matchSearch && matchType && matchStatus;
  });

  const handleAction = async () => {
    setActionLoading(true);
    try {
      const newStatus = actionType === 'suspend' ? 'suspended' : 'active';
      await updateDoc(doc(db, 'users', selected.id), {
        status: newStatus,
        suspendReason: actionType === 'suspend' ? 'Suspended by admin' : '',
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'auditLogs'), {
        action: 'USER ' + (actionType === 'suspend' ? 'SUSPENDED' : 'REACTIVATED'),
        details: 'User: ' + selected.name + ' | Type: ' + selected.type + ' | Phone: ' + selected.phone,
        adminEmail: 'nksapcolouracademy@gmail.com',
        targetId: selected.id, targetType: 'user',
        timestamp: serverTimestamp(),
      });
      setUsers(prev => prev.map(u => u.id === selected.id ? { ...u, status: newStatus } : u));
      message.success('User ' + (actionType === 'suspend' ? 'suspended' : 'reactivated') + ' — saved to Firebase');
      setActionModal(false); setViewModal(false);
    } catch (error) {
      message.error('Action failed');
    }
    setActionLoading(false);
  };

  const columns = [
    {
      title: 'User',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar style={{ background: (USER_TYPES[r.type]?.color || '#7A8A96') + '30', color: USER_TYPES[r.type]?.color || '#7A8A96', flexShrink: 0 }} size={36}>
            {r.name?.charAt(0)}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500, color: '#D4DCE3' }}>{r.name}</div>
            <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.phone} • {r.city}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      render: (type) => {
        const t = USER_TYPES[type] || { label: type, color: '#7A8A96' };
        return <Tag style={{ background: t.color + '20', color: t.color, border: '0.5px solid ' + t.color + '50', borderRadius: 20, fontSize: 11 }}>{t.label}</Tag>;
      },
    },
    {
      title: 'Business',
      dataIndex: 'businessName',
      render: (b) => <span style={{ color: '#7A8A96', fontSize: 12 }}>{b || '—'}</span>,
    },
    {
      title: 'KYC',
      dataIndex: 'kycStatus',
      render: (k) => {
        const colors = { approved: '#52C41A', pending: '#FAAD14', rejected: '#FF4D4F', not_required: '#7A8A96', not_submitted: '#4A4A47' };
        return <Tag style={{ background: (colors[k] || '#7A8A96') + '20', color: colors[k] || '#7A8A96', border: 'none', borderRadius: 20, fontSize: 11 }}>{k || 'N/A'}</Tag>;
      },
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      render: (d) => <span style={{ color: '#7A8A96', fontSize: 12 }}>{d}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => (
        <Badge status={status === 'active' ? 'success' : 'error'}
          text={<span style={{ color: status === 'active' ? '#52C41A' : '#FF4D4F', fontSize: 12 }}>
            {status?.charAt(0).toUpperCase() + status?.slice(1)}
          </span>} />
      ),
    },
    {
      title: 'Actions',
      render: (_, r) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} style={{ color: '#C9A84C' }}
            onClick={() => { setSelected(r); setViewModal(true); }} />
          {r.status === 'active' ? (
            <Button type="text" icon={<StopOutlined />} style={{ color: '#FF4D4F' }}
              onClick={() => { setSelected(r); setActionType('suspend'); setActionModal(true); }} />
          ) : (
            <Button type="text" icon={<CheckCircleOutlined />} style={{ color: '#52C41A' }}
              onClick={() => { setSelected(r); setActionType('reactivate'); setActionModal(true); }} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total Users', value: stats.total, color: '#C9A84C' },
          { title: 'Active', value: stats.active, color: '#52C41A' },
          { title: 'Suspended', value: stats.suspended, color: '#FF4D4F' },
          { title: 'Dealers', value: stats.dealers, color: '#1890FF' },
        ].map((stat, i) => (
          <Col xs={12} lg={6} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <Statistic title={<span style={{ color: '#7A8A96', fontSize: 12 }}>{stat.title}</span>}
                value={stat.value} valueStyle={{ color: stat.color, fontSize: 28, fontWeight: 600 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ ...CARD_STYLE, marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Search placeholder="Search name, phone, email..." value={searchText}
            onChange={e => setSearchText(e.target.value)} style={{ width: 260 }} />
          <Select value={filterType} onChange={setFilterType} style={{ width: 160 }}>
            <Option value="all">All Types</Option>
            {Object.entries(USER_TYPES).map(([key, val]) => <Option key={key} value={key}>{val.label}</Option>)}
          </Select>
          <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 140 }}>
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="suspended">Suspended</Option>
          </Select>
          <Button type="text" icon={<ReloadOutlined />} style={{ color: '#C9A84C' }} onClick={loadUsers}>Refresh</Button>
          <span style={{ marginLeft: 'auto', color: '#7A8A96', fontSize: 12 }}>
            {loading ? 'Loading...' : `${filtered.length} users`}
          </span>
        </div>
      </Card>

      <Card style={CARD_STYLE} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table dataSource={filtered} columns={columns} rowKey="id" pagination={{ pageSize: 10 }} />
        </Spin>
      </Card>

      <Modal title={<span style={{ color: '#C9A84C' }}>User Profile - {selected?.name}</span>}
        open={viewModal} onCancel={() => setViewModal(false)} width={580}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setViewModal(false)}>Close</Button>
            {selected?.status === 'active' ? (
              <Button danger icon={<StopOutlined />} onClick={() => { setActionType('suspend'); setActionModal(true); }}>Suspend</Button>
            ) : (
              <Button icon={<CheckCircleOutlined />}
                style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}
                onClick={() => { setActionType('reactivate'); setActionModal(true); }}>Reactivate</Button>
            )}
          </div>
        }>
        {selected && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Name">{selected.name}</Descriptions.Item>
            <Descriptions.Item label="Phone">{selected.phone}</Descriptions.Item>
            <Descriptions.Item label="Email" span={2}>{selected.email}</Descriptions.Item>
            <Descriptions.Item label="City">{selected.city}</Descriptions.Item>
            <Descriptions.Item label="District">{selected.district}</Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag style={{ background: (USER_TYPES[selected.type]?.color || '#7A8A96') + '20', color: USER_TYPES[selected.type]?.color || '#7A8A96', border: 'none', borderRadius: 20 }}>
                {USER_TYPES[selected.type]?.label || selected.type}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge status={selected.status === 'active' ? 'success' : 'error'} text={selected.status} />
            </Descriptions.Item>
            {selected.businessName && <Descriptions.Item label="Business" span={2}>{selected.businessName}</Descriptions.Item>}
            <Descriptions.Item label="KYC Status">{selected.kycStatus}</Descriptions.Item>
            <Descriptions.Item label="Verified">{selected.isVerified ? 'Yes' : 'No'}</Descriptions.Item>
            <Descriptions.Item label="Rating">{selected.rating || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Complaints">{selected.complaints || 0}</Descriptions.Item>
            <Descriptions.Item label="Joined">{selected.createdAt}</Descriptions.Item>
            <Descriptions.Item label="Source">{selected.appSource}</Descriptions.Item>
            {selected.suspendReason && (
              <Descriptions.Item label="Suspend Reason" span={2}>
                <span style={{ color: '#FF4D4F' }}>{selected.suspendReason}</span>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={<span style={{ color: actionType === 'suspend' ? '#FF4D4F' : '#52C41A' }}>
          {actionType === 'suspend' ? 'Suspend' : 'Reactivate'} - {selected?.name}
        </span>}
        open={actionModal} onCancel={() => setActionModal(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setActionModal(false)}>Cancel</Button>
            <Button loading={actionLoading} danger={actionType === 'suspend'}
              style={actionType !== 'suspend' ? { background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 } : {}}
              onClick={handleAction}>
              Confirm {actionType === 'suspend' ? 'Suspension' : 'Reactivation'}
            </Button>
          </div>
        }>
        <div style={{ padding: '16px 0', color: '#B0B8C1', fontSize: 13 }}>
          {actionType === 'suspend' ? 'User will be blocked immediately. Change saved to Firebase.' : 'User access will be restored. Change saved to Firebase.'}
        </div>
        <div style={{ padding: '12px 16px', background: 'rgba(201,168,76,0.08)', border: '0.5px solid rgba(201,168,76,0.2)', borderRadius: 8 }}>
          <div style={{ color: '#D4DCE3', fontWeight: 500 }}>{selected?.name}</div>
          <div style={{ color: '#7A8A96', fontSize: 12, marginTop: 4 }}>
            {USER_TYPES[selected?.type]?.label} • {selected?.city} • {selected?.phone}
          </div>
        </div>
      </Modal>
    </div>
  );
}